/**
 * Phase 20 Plan 20-02 — AlertsBulkActions
 *
 * Renders Acknowledge + Dismiss buttons for selected alert IDs.
 * Calls mutations in parallel via Promise.all.
 * Shows toast on completion and clears selection.
 */

import type { JSX } from 'react';

import { Button } from '@/components/ui/button';
import { useAcknowledgeMutation, useDismissMutation } from '@/hooks/useNotificationsQueries';
import { useAuth } from '@/lib/auth/AuthContext';
import { toast } from '@/lib/toast';

interface AlertsBulkActionsProps {
  readonly selectedIds: readonly number[];
  readonly onClearSelection: () => void;
}

export function AlertsBulkActions({
  selectedIds,
  onClearSelection,
}: AlertsBulkActionsProps): JSX.Element {
  const { permissions } = useAuth();
  const canAck = permissions.includes('notifications.acknowledge');
  const canDismiss = permissions.includes('notifications.dismiss');
  const ackMutation = useAcknowledgeMutation();
  const dismissMutation = useDismissMutation();

  const handleBulkAck = async (): Promise<void> => {
    await Promise.all(selectedIds.map((id) => ackMutation.mutateAsync(id)));
    toast.success(`${String(selectedIds.length)} alert${selectedIds.length !== 1 ? 's' : ''} acknowledged`);
    onClearSelection();
  };

  const handleBulkDismiss = async (): Promise<void> => {
    await Promise.all(selectedIds.map((id) => dismissMutation.mutateAsync(id)));
    toast.success(`${String(selectedIds.length)} alert${selectedIds.length !== 1 ? 's' : ''} dismissed`);
    onClearSelection();
  };

  if (selectedIds.length === 0) return <></>;

  return (
    <div className="flex items-center gap-2 py-2" data-testid="bulk-actions-bar">
      <span className="text-sm text-muted-foreground">{selectedIds.length} selected</span>
      {canAck && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => { void handleBulkAck(); }}
          disabled={ackMutation.isPending}
          data-testid="bulk-acknowledge-btn"
        >
          Acknowledge
        </Button>
      )}
      {canDismiss && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => { void handleBulkDismiss(); }}
          disabled={dismissMutation.isPending}
          data-testid="bulk-dismiss-btn"
        >
          Dismiss
        </Button>
      )}
    </div>
  );
}
